import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'app/store';
import { DrawerContent } from 'shared/ui/Drawer/DrawerStyles';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import { TabContext, TabPanel } from '@mui/lab';
import { StyledTab, StyledTabList } from 'shared/styles/StyledTabs';
import { useGetLanguagesQuery } from 'features/getLanguages/getLanguagesApi';
import { isEmptyArr, isEmptyObj } from 'shared/utils/isEmpty';
import ListItemText from '@mui/material/ListItemText';
import { useGetDirectionsQuery } from 'features/getDirections';
import { useUploadNewsImageMutation } from 'features/uploadNewsImage/uploadNewsImage';
import { useAddStoryMutation, useEditStoryMutation } from 'features/useStories';
import { ImageUpload } from 'shared/ui/ImageUpload/ui/ImageUpload';
import { useGetBrandsQuery } from 'features/useBrands';
import { StoryFormType } from 'widgets/content/types';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const StoriesForm = ({ item, languages, refetch }: { item?: any; languages: any; refetch: any }) => {
  const [addStory] = useAddStoryMutation();
  const [editStory] = useEditStoryMutation();
  const dispatch = useAppDispatch();

  const directions = useGetDirectionsQuery('directions');
  const brands = useGetBrandsQuery('brands');
  const [uploadFunc, uploadProps] = useUploadNewsImageMutation();

  const uploadImage = async (file: File, id: number, field: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const data = await uploadFunc(formData);
    // @ts-ignore
    if (data && data?.data) {
      // @ts-ignore
      setValue(`contents.${id}.${field}`, data?.data.file_path);
    }
  };

  const getDefaultValues = () => {
    const contents = languages.map(({ id }: { id: number }) => ({
      language_id: id,
      title: '',
      description: '',
      image: '',
    }));

    const result = contents
      .map((obj: any) => {
        const match = item.contents?.find((item: any) => item.language_id === obj.language_id);
        if (match) {
          return {
            id: match.id,
            language_id: obj.language_id,
            title: match.title,
            description: match.description,
            image: match.image,
          };
        }
        return obj;
      })
      .map((obj: any) => obj);

    const action_buttons = languages.map(({ id }: { id: number }) => ({
      language_id: id,
      label: '',
      link: '',
    }));

    const result2 = action_buttons
      .map((obj: any) => {
        const match = item.action_buttons?.find((item: any) => item.language_id === obj.language_id);
        if (match) {
          return {
            id: match.id,
            language_id: obj.language_id,
            label: match.label,
            link: match.link,
          };
        }
        return obj;
      })
      .map((obj: any) => obj);

    if (!isEmptyObj(item)) {
      return {
        directions: item.directions ? item.directions.map((obj: any) => obj.id) : [],
        directions_value: item.directions ? item.directions.map((obj: any) => obj.title) : [],
        contents: result,
        brand_id: item.brand_id,
        action_buttons: result2,
        priority: item.priority,
      };
    }

    return {
      directions: [],
      directions_value: [],
      brand_id: '',
      contents,
      priority: '',
      action_buttons: languages.map(({ id }: { id: number }) => ({
        language_id: id,
        label: '',
        link: '',
      })),
    };
  };

  const { control, getValues, setValue } = useForm<StoryFormType>({ defaultValues: getDefaultValues() });

  const [tabValue, setTabValue] = useState('0');

  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const onSendData = async () => {
    const body = getValues();

    const contents = body.contents.filter((item) => item.title !== '' || item.description !== '' || item.image !== '');

    const newBody = { ...body, contents, priority: +body.priority };

    const response = !isEmptyObj(item)
      ? await editStory({
          id: item.id,
          body: newBody,
        })
      : await addStory({ body: newBody });
    if (response) {
      dispatch(setDrawerOpen(false));
      refetch();
    }
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <div className="content">
      {!brands.isLoading ? (
        !isEmptyArr(brands.data.items) && (
          <Controller
            name="brand_id"
            control={control}
            render={({ field }) => (
              <TextField
                select
                type="text"
                label="Бренды"
                placeholder=""
                fullWidth
                sx={{ marginBottom: '24px' }}
                {...field}
              >
                {brands.data.items.map((item: any) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                  >
                    {item.title}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )
      ) : (
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      )}

      <FormControl sx={{ width: '100%' }}>
        <Controller
          name="directions_value"
          control={control}
          rules={{ required: 'Направления обязательны' }}
          render={({ field: { value, onChange } }) => (
            <>
              <InputLabel id="canals-label">Направления</InputLabel>
              <Select
                labelId="canals-label"
                id="canals-multiple-checkbox"
                multiple
                sx={{ marginBottom: '24px' }}
                value={value}
                onChange={onChange}
                fullWidth
                input={<OutlinedInput label="Направления" />}
                MenuProps={MenuProps}
                renderValue={(selected) => selected?.map((option) => option).join(', ')}
              >
                {directions.data ? (
                  directions.data.map((item: any) => (
                    <MenuItem
                      key={item.id}
                      value={item.title}
                      onClick={() => {
                        const { directions } = getValues();
                        if (directions.includes(item.id)) {
                          const new_values = directions.filter((i) => i !== item.id);
                          setValue('directions', new_values);
                          return;
                        }
                        setValue('directions', [...directions, item.id]);
                      }}
                    >
                      <Checkbox checked={value.includes(item.title)} />
                      <ListItemText primary={item.title} />
                    </MenuItem>
                  ))
                ) : (
                  <div>Loading</div>
                )}
              </Select>
            </>
          )}
        />
      </FormControl>

      <Controller
        control={control}
        name="priority"
        render={({ field }) => (
          <TextField
            value={field.value}
            onChange={(e) => field.onChange(e)}
            label="Приоритет"
            fullWidth
            type="number"
            sx={{ marginBottom: '24px' }}
          />
        )}
      />

      <Controller
        control={control}
        name="language_id"
        rules={{ required: 'Oбязательна' }}
        render={() => (
          <Box sx={{ width: '100%' }}>
            <TabContext value={tabValue}>
              <StyledTabList
                onChange={handleChangeTabValue}
                aria-label="basic tabs example"
              >
                <StyledTab
                  value="0"
                  label="RU"
                  {...a11yProps(0)}
                />
                <StyledTab
                  value="1"
                  label="KZ"
                  {...a11yProps(1)}
                />
                <StyledTab
                  value="2"
                  label="KG"
                  {...a11yProps(2)}
                />
                <StyledTab
                  value="3"
                  label="UZ"
                  {...a11yProps(3)}
                />
              </StyledTabList>
              {getValues().contents.map((item: any, index: number) => (
                <TabPanel
                  key={index}
                  value={`${index}`}
                  sx={{ paddingLeft: '0px' }}
                >
                  <Controller
                    control={control}
                    name={`contents.${index}.title`}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={(e) => field.onChange(e)}
                        label="Заголовок"
                        fullWidth
                        sx={{ marginBottom: '24px' }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`contents.${index}.description`}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={(e) => field.onChange(e)}
                        label="Описание"
                        fullWidth
                        sx={{ marginBottom: '24px' }}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`contents.${index}.image`}
                    render={({ field }) => (
                      <ImageUpload
                        title="Фото"
                        src={field.value}
                        callback={(val) => uploadImage(val, index, 'image')}
                        isLoading={uploadProps.isLoading}
                        isSuccess={uploadProps.isSuccess}
                        silent
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`action_buttons.${index}.label`}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        label="Название кнопки"
                        fullWidth
                        sx={{ marginBottom: '24px' }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`action_buttons.${index}.link`}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        label="URL кнопки"
                        fullWidth
                        sx={{ marginBottom: '24px' }}
                      />
                    )}
                  />
                </TabPanel>
              ))}
            </TabContext>
          </Box>
        )}
      />

      <Button
        onClick={onSendData}
        variant="contained"
        sx={{ textTransform: 'none' }}
      >
        {!isEmptyObj(item) ? 'Изменить' : 'Создать'}
      </Button>
    </div>
  );
};

export const StoriesFormWidget = ({ detail, refetch }: { detail?: any; refetch: any }) => {
  const dispatch = useAppDispatch();
  const { isDrawerOpen } = useAppSelector((state) => state.ui);

  const { data: languages, isSuccess: isSuccessLanguages } = useGetLanguagesQuery('languages');

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => dispatch(setDrawerOpen(false))}
    >
      <DrawerContent style={{ width: '800px' }}>
        <div className="header">
          <Typography
            variant="h5"
            sx={{ fontWeight: '600' }}
          >
            {!isEmptyObj(detail) ? 'Изменить' : 'Создать'} сторис
          </Typography>

          <IconButton
            onClick={() => {
              dispatch(setDrawerOpen(false));
            }}
            sx={{ color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {isSuccessLanguages ? (
          <StoriesForm
            languages={languages}
            refetch={refetch}
            item={detail}
          />
        ) : (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        )}
      </DrawerContent>
    </Drawer>
  );
};
